import { useMemo } from "react";
import { useParams, useLocation } from "react-router-dom";

import type { AnalyticsEventContext } from "../services/analytics-api";
import type { AnalyticsOptions } from "./useAnalytics";
import { useAnalytics } from "./useAnalytics";

export interface ContextualAnalyticsProps {
  file?: number | string;
  lien?: number | string;
  lien_file?: number | string;
  msa?: number | string;
  cost_projection?: number | string;
  amended_review_screening?: number | string;
  background_job?: number | string;
  claim?: number | string;
  rre?: number | string;
  client?: number | string;
  claimant?: number | string;
  employer?: number | string;
  adjuster?: number | string;
  tpa?: number | string;
  meta?: Record<string, unknown>;
}

/**
 * Hook that automatically extracts context from URL params and provides
 * analytics tracking with entity context.
 */
export const useContextualAnalytics = (
  props: ContextualAnalyticsProps = {},
) => {
  const params = useParams();
  const location = useLocation();

  // Extract IDs from URL params and path segments
  const urlContext = useMemo(() => {
    const context: AnalyticsEventContext = {};

    // Map common URL parameter names to analytics context fields
    const paramMappings: Record<string, keyof AnalyticsEventContext> = {
      fileId: "file",
      lienId: "lien",
      msaId: "msa",
      backgroundJobId: "background_job",
      claimId: "claim",
      rreId: "rre",
      clientId: "client",
      claimantId: "claimant",
      employerId: "employer",
      adjusterId: "adjuster",
      tpaId: "tpa",
      // Also check without 'Id' suffix
      file: "file",
      lien: "lien",
      msa: "msa",
      claim: "claim",
      rre: "rre",
      client: "client",
      claimant: "claimant",
      employer: "employer",
      adjuster: "adjuster",
      tpa: "tpa",
    };

    // Extract context from URL params first
    Object.entries(paramMappings).forEach(([paramName, contextField]) => {
      if (params[paramName]) {
        const value = parseInt(params[paramName] as string, 10);
        if (!isNaN(value)) {
          context[contextField] = value;
        }
      }
    });

    // Extract file_id from query parameters
    const urlParams = new URLSearchParams(location.search);
    const fileIdFromQuery = urlParams.get("file_id");
    if (fileIdFromQuery) {
      const fileId = parseInt(fileIdFromQuery, 10);
      if (!isNaN(fileId)) {
        context.file = fileId;
      }
    }

    // Extract context from URL path segments
    // Examples:
    // /operations/files/279/edit -> file: 279
    // /operations/claimants/3/overview -> claimant: 3
    // /operations/clients/81/overview -> client: 81
    // /liens/8/lien/1 -> lien_file: 8, lien: 1
    // /liens/8/overview -> lien_file: 8
    const pathSegments = location.pathname.split("/").filter(Boolean);

    // Handle special liens URL patterns first
    if (pathSegments[0] === "liens" && pathSegments.length >= 2) {
      const lienFileId = parseInt(pathSegments[1], 10);
      if (!isNaN(lienFileId)) {
        context.lien_file = lienFileId;

        // Check for nested lien pattern: /liens/{lien_file_id}/lien/{lien_id}
        if (pathSegments.length >= 4 && pathSegments[2] === "lien") {
          const lienId = parseInt(pathSegments[3], 10);
          if (!isNaN(lienId)) {
            context.lien = lienId;
          }
        }
      }
    } else {
      // Check for Future Medical service URLs first
      const futureMedicalMatch = pathSegments.findIndex(
        (segment) => segment === "future-medical",
      );
      if (
        futureMedicalMatch !== -1 &&
        pathSegments.length >= futureMedicalMatch + 3
      ) {
        const serviceType = pathSegments[futureMedicalMatch + 1];
        const serviceIdStr = pathSegments[futureMedicalMatch + 2];
        const serviceId = parseInt(serviceIdStr, 10);

        if (!isNaN(serviceId)) {
          // Map Future Medical service types to context fields
          switch (serviceType) {
            case "msa":
              if (context.msa === undefined) {
                context.msa = serviceId;
              }
              break;
            case "lcp":
            case "mcp":
              if (context.cost_projection === undefined) {
                context.cost_projection = serviceId;
              }
              break;
            case "ar-screening":
              if (context.amended_review_screening === undefined) {
                context.amended_review_screening = serviceId;
              }
              break;
          }
        }
      }

      // Handle other standard entity patterns
      const pathEntityMappings: Record<string, keyof AnalyticsEventContext> = {
        files: "file",
        liens: "lien",
        msas: "msa",
        jobs: "background_job",
        claims: "claim",
        rres: "rre",
        clients: "client",
        claimants: "claimant",
        employers: "employer",
        adjusters: "adjuster",
        tpas: "tpa",
      };

      // Look for patterns like /entity-type/id/
      for (let i = 0; i < pathSegments.length - 1; i++) {
        const segment = pathSegments[i];
        const nextSegment = pathSegments[i + 1];

        if (pathEntityMappings[segment]) {
          const id = parseInt(nextSegment, 10);
          if (!isNaN(id)) {
            const contextField = pathEntityMappings[segment];
            // Only set if we haven't already extracted this context from query parameters
            if (context[contextField] === undefined) {
              context[contextField] = id;
            }
          }
        }
      }
    }

    // Debug logging (uncomment to debug context extraction)
    // console.log('Analytics Context Debug:', {
    //   pathname: location.pathname,
    //   pathSegments,
    //   extractedContext: context,
    //   params
    // });

    return context;
  }, [params, location.pathname, location.search]);

  // Merge URL context with explicitly provided props
  const context = useMemo(() => {
    const propsContext: AnalyticsEventContext = {};

    // Convert string values to numbers where provided
    Object.entries(props).forEach(([key, value]) => {
      if (key !== "meta" && value != null) {
        const numValue =
          typeof value === "string" ? parseInt(value, 10) : value;
        if (!isNaN(numValue as number)) {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          (propsContext as any)[key] = numValue;
        }
      }
    });

    return {
      ...urlContext,
      ...propsContext,
    };
  }, [urlContext, props]);

  const options: AnalyticsOptions = {
    context,
    meta: props.meta,
  };

  const analytics = useAnalytics(options);

  return {
    ...analytics,
    context,
    /**
     * Track a page view with the current context
     */
    trackContextualPageView: (
      url?: string,
      additionalMeta?: Record<string, unknown>,
    ) => {
      analytics.trackPageView(url, additionalMeta);
    },
    /**
     * Track an event with the current context
     */
    trackContextualEvent: (
      type: string,
      url?: string,
      additionalMeta?: Record<string, unknown>,
      additionalContext?: AnalyticsEventContext,
    ) => {
      analytics.trackEvent(type, url, additionalMeta, additionalContext);
    },
  };
};
